import React, { useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BlackoutDatesSettings = ({ settings, setSettings, updateSettings }) => {
  const [selectedDate, setSelectedDate] = useState('');

  // Memoise the sorted list. Also avoids `.sort()` mutating the underlying
  // state array in place on every render — a subtle bug in the old code.
  const sortedBlackoutDates = useMemo(() => {
    const dates = settings?.blackout_dates || [];
    return [...dates].sort();
  }, [settings?.blackout_dates]);

  const addBlackoutDate = () => {
    if (selectedDate) {
      const currentDates = settings.blackout_dates || [];
      if (!currentDates.includes(selectedDate)) {
        setSettings({
          ...settings,
          blackout_dates: [...currentDates, selectedDate].sort()
        });
        setSelectedDate('');
      } else {
        alert('This date is already blocked');
      }
    }
  };

  const removeBlackoutDate = (dateToRemove) => {
    const currentDates = settings.blackout_dates || [];
    setSettings({
      ...settings,
      blackout_dates: currentDates.filter(date => date !== dateToRemove)
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: '1.25rem' }}>📅 Blackout Dates</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            Block specific dates when you're unavailable (holidays, vacations, personal days).
            Customers won't be able to book pickups on these dates.
          </p>
          
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            />
            <Button
              onClick={addBlackoutDate}
              disabled={!selectedDate}
              style={{
                backgroundColor: selectedDate ? '#ef4444' : '#9ca3af',
                color: 'white',
                padding: '0.75rem 1.5rem'
              }}
            >
              Block Date
            </Button>
          </div>

          {sortedBlackoutDates.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto' }}>
              {sortedBlackoutDates.map(date => (
                <div
                  key={date}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <span style={{ fontWeight: '600', color: '#991b1b' }}>
                    🚫 {formatDate(date)}
                  </span>
                  <button
                    onClick={() => removeBlackoutDate(date)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      padding: '0',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              backgroundColor: '#f9fafb', 
              borderRadius: '6px',
              color: '#6b7280'
            }}>
              No blackout dates set. You're available every day!
            </div>
          )}
        </div>

        <Button
          onClick={() => updateSettings(settings)}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            padding: '0.75rem 1.5rem',
            marginTop: '1rem'
          }}
        >
          Save Blackout Dates
        </Button>
      </CardContent>
    </Card>
  );
};

export default BlackoutDatesSettings;
